import React, { useState } from 'react';
import { Calendar, Edit, Plus, Save, Trash2 } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import Modal from '../ui/Modal';
import { invalidateMarkerCache } from '../../utils/calenderMarkerUtils';
import { useConfirm } from '../ui/ConfirmDialog';
import { useToast } from '../ToastProvider';

// #312: Reiter „Kalender-Marker“ des Admin-Bereichs, Code unverändert aus
// AdminPanel.js übernommen (Muster und Begründung: admin/LogsTab.js).
//
// Die Marker lädt `loadData()` im Panel und reicht sie über den Setter herein.
export function useMarkersTab({ setError, setSaving }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  // Calendar Marker State
  const [calendarMarkers, setCalendarMarkers] = useState([]);
  const [showAddMarkerForm, setShowAddMarkerForm] = useState(false);
  const [editingMarker, setEditingMarker] = useState(null);
  const [newMarker, setNewMarker] = useState({
    value: '',
    label: '',
    icon: '',
    color: 'var(--color-primary)',
    isActive: true,
    sortOrder: 0
  });

  // Calendar Marker Handler Functions
  const handleCreateCalendarMarker = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await apiService.createCalendarMarker(newMarker);
      
      // Invalidate cache and reload data
      invalidateMarkerCache();
      const markersResponse = await apiService.getCalendarMarkersAdmin();
      setCalendarMarkers(markersResponse.markers || []);
      
      // Reset form
      setNewMarker({
        value: '',
        label: '',
        icon: '',
        color: 'var(--color-primary)',
        isActive: true,
        sortOrder: 0
      });
      setShowAddMarkerForm(false);
      
      pushToast({ message: 'Kalender Marker erfolgreich erstellt.', type: 'success' });
    } catch (error) {
      console.error('Error creating calendar marker:', error);
      setError(t('admin.markers.createFailed', 'Fehler beim Erstellen des Kalender-Markers.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCalendarMarker = async (markerId, updates) => {
    setSaving(true);
    setError(null);
    
    try {
      await apiService.updateCalendarMarker(markerId, updates);
      
      // Invalidate cache and update local state
      invalidateMarkerCache();
      setCalendarMarkers(prevMarkers => 
        prevMarkers.map(marker => 
          marker.id === markerId ? { ...marker, ...updates } : marker
        )
      );
      
      pushToast({ message: 'Kalender Marker erfolgreich aktualisiert.', type: 'success' });
    } catch (error) {
      console.error('Error updating calendar marker:', error);
      setError(t('admin.markers.updateFailed', 'Fehler beim Aktualisieren des Kalender-Markers.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCalendarMarker = async (markerId) => {
    if (!(await confirm({ message: t('admin.markers.deleteConfirm', 'Sind Sie sicher, dass Sie diesen Kalender-Marker löschen möchten?'), danger: true }))) {
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      await apiService.deleteCalendarMarker(markerId);
      
      // Invalidate cache and remove from local state
      invalidateMarkerCache();
      setCalendarMarkers(prevMarkers => 
        prevMarkers.filter(marker => marker.id !== markerId)
      );
      
      pushToast({ message: 'Kalender Marker erfolgreich gelöscht.', type: 'success' });
    } catch (error) {
      console.error('Error deleting calendar marker:', error);
      setError(t('admin.markers.deleteFailed', 'Fehler beim Löschen des Kalender-Markers.'));
    } finally {
      setSaving(false);
    }
  };

  return { calendarMarkers, setCalendarMarkers, showAddMarkerForm, setShowAddMarkerForm, editingMarker, setEditingMarker, newMarker, setNewMarker, handleCreateCalendarMarker, handleUpdateCalendarMarker, handleDeleteCalendarMarker };
}

const MarkersTab = ({ tab, saving }) => {
  const { t } = useI18n();
  const { calendarMarkers, showAddMarkerForm, setShowAddMarkerForm, editingMarker, setEditingMarker, newMarker, setNewMarker, handleCreateCalendarMarker, handleUpdateCalendarMarker, handleDeleteCalendarMarker } = tab;

  return (
    <div className="admin-section">
      <h3>{t('admin.markers.heading', 'Kalender Marker Konfiguration')}</h3>
      <p className="section-description">
        {t('admin.markers.desc', 'Verwalten Sie die verfügbaren Kalender Marker für Tickets. Diese Marker können von allen Benutzern verwendet werden.')}
      </p>

      {/* Marker Liste */}
      <div className="markers-list">
        <h4>{t('admin.markers.current', 'Aktuelle Marker')}</h4>
        
        {calendarMarkers.length === 0 ? (
          <div className="no-data">
            <Calendar size={48} style={{ color: 'var(--color-text-disabled)' }} />
            <p>{t('admin.markers.none', 'Noch keine Kalender Marker konfiguriert.')}</p>
          </div>
        ) : (
          <div className="markers-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('admin.markers.col.value', 'Wert')}</th>
                  <th>{t('admin.markers.col.label', 'Label')}</th>
                  <th>{t('admin.markers.col.icon', 'Symbol')}</th>
                  <th>{t('admin.markers.col.color', 'Farbe')}</th>
                  <th>{t('admin.markers.col.status', 'Status')}</th>
                  <th>{t('admin.markers.col.sort', 'Sortierung')}</th>
                  <th>{t('admin.markers.col.actions', 'Aktionen')}</th>
                </tr>
              </thead>
              <tbody>
                {calendarMarkers.map(marker => (
                  <tr key={marker.id}>
                    <td className="marker-value">
                      <code>{marker.value}</code>
                    </td>
                    <td className="marker-label">{marker.label}</td>
                    <td className="marker-icon">
                      <span style={{ fontSize: '1.5em' }}>{marker.icon}</span>
                    </td>
                    <td className="marker-color">
                      <div className="color-display">
                        <div 
                          className="color-box" 
                          style={{ backgroundColor: marker.color }}
                        ></div>
                        <span>{marker.color}</span>
                      </div>
                    </td>
                    <td>
                      <label className="status-toggle">
                        <input
                          type="checkbox"
                          checked={marker.isActive}
                          onChange={(e) => handleUpdateCalendarMarker(marker.id, { isActive: e.target.checked })}
                          disabled={saving}
                        />
                        <span className={`toggle-slider ${marker.isActive ? 'enabled' : 'disabled'}`}></span>
                        <span className="status-text">{marker.isActive ? t('common.active', 'Aktiv') : t('common.inactive', 'Inaktiv')}</span>
                      </label>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={marker.sortOrder}
                        onChange={(e) => handleUpdateCalendarMarker(marker.id, { sortOrder: parseInt(e.target.value) })}
                        disabled={saving}
                        className="sort-input"
                        min="0"
                      />
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => setEditingMarker(marker)}
                          className="btn btn-sm btn-secondary"
                          disabled={saving}
                          title={t('common.edit', 'Bearbeiten')} aria-label={t('common.edit', 'Bearbeiten')}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteCalendarMarker(marker.id)}
                          className="btn btn-sm btn-danger"
                          disabled={saving}
                          title={t('common.delete', 'Löschen')} aria-label={t('common.delete', 'Löschen')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Neuen Marker hinzufügen */}
      <div className="marker-actions">
        <button
          onClick={() => setShowAddMarkerForm(!showAddMarkerForm)}
          className="btn btn-primary"
          disabled={saving}
        >
          <Plus size={16} />
          {t('admin.markers.addButton', 'Marker hinzufügen')}
        </button>
      </div>

      {/* Formular für neuen Marker */}
      {showAddMarkerForm && (
        <div className="add-marker-form">
          <h4>{t('admin.markers.addHeading', 'Neuen Kalender Marker hinzufügen')}</h4>
          <form onSubmit={handleCreateCalendarMarker}>
            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.markers.valueLabel', 'Wert (eindeutig)')}:</label>
                <input
                  type="text"
                  value={newMarker.value}
                  onChange={(e) => setNewMarker({...newMarker, value: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')})}
                  required
                  placeholder={t('admin.markers.valuePlaceholder', 'z.B. extern, intern')}
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.markers.labelLabel', 'Label')}:</label>
                <input
                  type="text"
                  value={newMarker.label}
                  onChange={(e) => setNewMarker({...newMarker, label: e.target.value})}
                  required
                  placeholder={t('admin.markers.labelPlaceholder', 'z.B. Extern, Intern')}
                  disabled={saving}
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.markers.iconLabel', 'Symbol (Emoji)')}:</label>
                <input
                  type="text"
                  value={newMarker.icon}
                  onChange={(e) => setNewMarker({...newMarker, icon: e.target.value})}
                  required
                  placeholder="🚗"
                  disabled={saving}
                  maxLength="4"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.markers.colorLabel', 'Farbe')}:</label>
                <input
                  type="color"
                  value={newMarker.color}
                  onChange={(e) => setNewMarker({...newMarker, color: e.target.value})}
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.markers.sortLabel', 'Sortierung')}:</label>
                <input
                  type="number"
                  value={newMarker.sortOrder}
                  onChange={(e) => setNewMarker({...newMarker, sortOrder: parseInt(e.target.value)})}
                  disabled={saving}
                  min="0"
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !newMarker.value || !newMarker.label || !newMarker.icon}
              >
                <Save size={16} />
                {t('admin.markers.create', 'Marker erstellen')}
              </button>
              <button
                type="button"
                onClick={() => setShowAddMarkerForm(false)}
                className="btn btn-secondary"
                disabled={saving}
              >
                {t('admin.markers.cancel', 'Abbrechen')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Marker Modal */}
      {editingMarker && (
        <Modal open onClose={() => setEditingMarker(null)} title={t('admin.markers.editHeading', 'Marker bearbeiten')} maxWidth="480px">
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateCalendarMarker(editingMarker.id, {
                label: editingMarker.label,
                icon: editingMarker.icon,
                color: editingMarker.color
              });
              setEditingMarker(null);
            }}>
              <div className="form-group">
                <label>{t('admin.markers.valueReadonly', 'Wert (nicht änderbar)')}:</label>
                <input
                  type="text"
                  value={editingMarker.value}
                  disabled
                  className="disabled-input"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.markers.labelLabel', 'Label')}:</label>
                <input
                  type="text"
                  value={editingMarker.label}
                  onChange={(e) => setEditingMarker({...editingMarker, label: e.target.value})}
                  required
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.markers.iconLabel', 'Symbol (Emoji)')}:</label>
                <input
                  type="text"
                  value={editingMarker.icon}
                  onChange={(e) => setEditingMarker({...editingMarker, icon: e.target.value})}
                  required
                  disabled={saving}
                  maxLength="4"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.markers.colorLabel', 'Farbe')}:</label>
                <input
                  type="color"
                  value={editingMarker.color}
                  onChange={(e) => setEditingMarker({...editingMarker, color: e.target.value})}
                  disabled={saving}
                />
              </div>
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  <Save size={16} />
                  {t('common.save', 'Speichern')}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingMarker(null)}
                  className="btn btn-secondary"
                  disabled={saving}
                >
                  {t('common.cancel', 'Abbrechen')}
                </button>
              </div>
            </form>
        </Modal>
      )}
    </div>
  );
};

export default MarkersTab;
